import { useTranslation } from "react-i18next";
import {
	FrontendOrganizationLicense,
	FrontendSampleInstance,
	FrontendCompanyTestCategory,
	FrontendUserInfo,
	FrontendSampleTestInformation,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
} from "node-library/dist/portal";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import SamplePreviewTable from "./samplePreviewTable";
import { RowDropdown } from "../../../common-components/inputs";
import { Divider } from "@mui/material";
import {
	BlackButton,
	CustomIconButton,
	DropdownButton,
	GreenButton,
} from "../../../common-components/button";
import AddIcon from "@mui/icons-material/Add";
import { SaveIcon } from "../../../common-components/svgIcons";
import ClearIcon from "@mui/icons-material/Clear";
import { NeutralColor } from "node-library/dist/colors";
import { SetStateAction, useState } from "react";
import DiscardOrderConfirmModal from "../../../common-components/modals/discardOrderConfirmModal";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import NewOrderSkeletonUI from "./skeletonUI/newOrderSkeleton";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { Severity, SystemRole } from "node-library/dist/constants";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { FrontendExpectationInputState } from "node-library/dist/frontendInputStates";
import EditInstanceExpectationModal from "../../../common-components/modals/editInstanceExpectationModal";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	orderTestCategory: FrontendCompanyTestCategory;

	licenseLoading: boolean;
	isEditOrder?: boolean;

	goBackToPreviousPage?: () => void;
	sampleInstances: Array<FrontendSampleInstance>;
	setSampleInstances: React.Dispatch<
		React.SetStateAction<FrontendSampleInstance[]>
	>;
	deleteSample: (sampleId: string) => void;
	duplicateSample: (sampleToUpdate: FrontendSampleInstance) => void;
	goToUpdateSample: (sample: FrontendSampleInstance) => void;
	goToAddNewSample: () => void;
	goToSubmitOrder: () => void;
	discardOrder: () => Promise<void>;
	userLicenses: Array<FrontendOrganizationLicense>;
	licenseId: string;
	setLicenseId: React.Dispatch<React.SetStateAction<string>>;
	licenseError: boolean;
	setLicenseError: React.Dispatch<React.SetStateAction<boolean>>;
	needToReviewAllInstances: boolean;
	isImportMode?: boolean;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	setSampleSpecifications: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecification[]>
	>;
	setImportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function NewOrderContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		sampleInstances,
		setSampleInstances,
		deleteSample,
		goToUpdateSample,
		goToAddNewSample,
		goToSubmitOrder,
		duplicateSample,
		discardOrder,
		userLicenses,
		licenseId,
		setLicenseId,
		licenseError,
		setLicenseError,
		needToReviewAllInstances,
		isImportMode,
		orderTestCategory,
		specDirectories,
		setSampleSpecifications,
		setImportModalOpen,
		setUnsavedChanges,
	} = props;

	// const specificationError: boolean = sampleBatches.some((batch) => {
	// 	return batch.specificationId && !batch.specificationActive;
	// });
	const doesUserHaveDeleteAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.delete,
			)
		: false;

	const doesUserHaveCreateOrEditAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.update,
			) ||
			hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.orders.create,
			)
		: false;

	//confirm modals
	const [discardOrderConfirmOpen, setDiscardOrderConfirmOpen] =
		useState<boolean>(false);

	//sales-rep view
	const [selectedSampleInstance, setSelectedSampleInstance] = useState<
		FrontendSampleInstance | undefined
	>();
	const [editExpectationModalOpen, setEditExpectationModalOpen] =
		useState<boolean>(false);

	const fieldsHavingExpectations: FrontendCompanyTestResultField[] =
		company.testResultAndExpectationFields.filter(
			(field) => field.expectationField !== undefined,
		);

	const selectedNodeIds: string[] =
		selectedSampleInstance?.sampleTestInfo?.selectedTests.map(
			(test) => test.selectedNodeId,
		) || [];

	const fieldsNeedingExpectations: FrontendCompanyTestResultField[] =
		fieldsHavingExpectations
			.filter((resultField) => {
				// 1. Get the actual package objects from the company data using the IDs stored in the field
				const associatedPackages: Array<FrontendCompanyResultFieldHierarchyNodeMappingPackage> =
					company.hierarchyNodeMappingPackages.filter((pkg) =>
						resultField.nodeMappingPackageIds.includes(pkg.packageId),
					);

				// 2. Check if ANY of those packages have ALL their required nodes selected
				return associatedPackages.some((mappingPackage) =>
					mappingPackage.mappingNodeIds.every((id) =>
						selectedNodeIds.includes(id),
					),
				);
			})
			.sort((a, b) => a.displayOrder - b.displayOrder);

	function openEditExpectationModal(sampleInstance: FrontendSampleInstance) {
		setSelectedSampleInstance(sampleInstance);
		setEditExpectationModalOpen(true);
	}

	function saveExpectations(expectationInputs: FrontendExpectationInputState) {
		if (!selectedSampleInstance || !selectedSampleInstance.sampleInstanceId) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
			return;
		}

		const updatedInstances: Array<FrontendSampleInstance> = sampleInstances.map(
			(instance) => {
				if (
					instance.sampleInstanceId === selectedSampleInstance.sampleInstanceId
				) {
					const updatedTestInfo: FrontendSampleTestInformation = {
						...(instance.sampleTestInfo as FrontendSampleTestInformation),
						expectationInputs: expectationInputs,
					};

					return {
						...instance,
						sampleTestInfo: updatedTestInfo,
					};
				}
				return instance;
			},
		);

		setSampleInstances(updatedInstances);
		setEditExpectationModalOpen(false);
		setSelectedSampleInstance(undefined);
	}

	const licenseOptions: Array<{
		value: any;
		title: string;
		renderValue?: string;
	}> = userLicenses.map((license) => {
		return {
			value: license.licenseId,
			title: license.licenseNumber,
		};
	});
	licenseOptions.push({
		value: "",
		title: translation("NoLicense"),
		renderValue: translation("NoLicense"),
	});

	const licenseDropdown: React.ReactNode = (
		<RowDropdown
			id="license-selection-dropdown"
			className="license-selection-dropdown"
			label={translation("Licenseforthisorder")}
			labelClassName="typography-heading-03"
			options={licenseOptions}
			value={licenseId}
			setValue={(value: string) => {
				setLicenseId(value);
				setLicenseError(false);
			}}
			hasError={licenseError}
			displayEmpty
		></RowDropdown>
	);

	const buttonPanel: React.ReactNode = (
		<div className="new-orders-button-panel">
			<div className="new-order-flexbutton-container">
				{doesUserHaveCreateOrEditAccess && (
					<BlackButton
						id={"add-sample-button"}
						size={"flex"}
						buttonClassName="button-with-left-icon"
						onClick={() => {
							goToAddNewSample();
						}}
					>
						<AddIcon />
						<span
						// className="new-order-collapsible-text"
						>
							{translation("SampleInstance")}
						</span>
					</BlackButton>
				)}
			</div>

			<div className="new-orders-button-sub-panel-right">
				<div>
					{!(props.isEditOrder && !doesUserHaveDeleteAccess) && (
						<DropdownButton
							buttonColor="grey"
							buttonClassName="new-order-collapible-button"
							id={"new-orders-more-actions"}
							menuOptions={[
								{
									key: "import-order",
									onClick: () => {
										setImportModalOpen(true);
									},
									children: (
										<div className="icon-dropdown-menu-item">
											<KeyboardArrowDownIcon sx={{ fontSize: "20px" }} />
											{translation("Importorder")}
										</div>
									),
								},
								{
									key: "discard-order",
									onClick: () => {
										setDiscardOrderConfirmOpen(true);
									},
									children: (
										<div className="icon-dropdown-menu-item">
											<ClearIcon sx={{ fontSize: "20px" }} />
											{translation("Discardorder")}
										</div>
									),
								},
							]}
							onClick={(e) => {}}
						>
							<span className="new-order-collapsible-text">
								{translation("More")}
							</span>
						</DropdownButton>
					)}
				</div>
				<div className="new-order-flexbutton-container">
					<GreenButton
						size={"flex"}
						id={"submit-order-button"}
						onClick={() => {
							// if (specificationError && !props.isInAdminView) {
							// 	openSnackbar(
							// 		translation("ActionRequired"),
							// 		Severity.ERROR,
							// 		translation("SpecificationErrorDescription"),
							// 		<RedErrorIcon />
							// 	);
							// 	return;
							// }
							goToSubmitOrder();
						}}
						disabled={
							sampleInstances.length === 0 ||
							licenseError ||
							needToReviewAllInstances
						}
					>
						{translation("ReviewOrder")}
					</GreenButton>
				</div>
			</div>
		</div>
	);
	function updateSpecification(specification: FrontendSampleSpecification) {
		setSampleSpecifications((prevSpecs) => {
			// ADD 'return' here
			return prevSpecs.map((spec) => {
				if (spec.specificationId === specification.specificationId) {
					return specification;
				}
				return spec;
			});
		});
	}
	return (
		<>
			{props.licenseLoading ? (
				<NewOrderSkeletonUI />
			) : (
				<div style={{ margin: "0 auto" }}>
					{
						<div>
							<div
								className="typography-heading-01 "
								style={{ margin: "20px 0px" }}
							>
								<div style={{ display: "flex", gap: "5px" }}>
									{
										<CustomIconButton
											id="admin-view-back-to-order-details-button"
											size="small"
											onClick={() => {
												props.goBackToPreviousPage!();
											}}
											icon={<KeyboardArrowLeftIcon />}
										></CustomIconButton>
									}
									{`${
										props.isEditOrder
											? translation("EditOrder")
											: translation("NewOrder")
									} (${props.orderTestCategory.testCategoryName})`}
								</div>
							</div>
							{licenseDropdown}
							<Divider
								orientation="horizontal"
								flexItem
								sx={{ my: 2 }}
							></Divider>
							{buttonPanel}
							{sampleInstances.length === 0 ? (
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "10px",
										marginTop: "10%",
										alignItems: "center",
										color: NeutralColor.NEUTRAL_GREY_300,
									}}
								>
									<div className="typography-heading-03">
										{translation("NoSampleInOrder")}
									</div>
									<div className="typography-body-01">
										{translation("NoSampleDescription")}
									</div>
								</div>
							) : (
								<SamplePreviewTable
									company={company}
									setCompany={setCompany}
									user={props.user}
									isImportMode={isImportMode}
									orderTestCategory={orderTestCategory}
									sampleInstances={sampleInstances}
									deleteSample={deleteSample}
									goToUpdateSample={goToUpdateSample}
									duplicateSample={duplicateSample}
									openEditExpectationModal={openEditExpectationModal}
									specDirectories={specDirectories}
									updateSpecification={updateSpecification}
								/>
							)}
						</div>
					}
					<DiscardOrderConfirmModal
						open={discardOrderConfirmOpen}
						setOpen={setDiscardOrderConfirmOpen}
						proceedAction={() => {
							discardOrder();
						}}
					></DiscardOrderConfirmModal>
					<EditInstanceExpectationModal
						company={company}
						open={editExpectationModalOpen}
						setOpen={setEditExpectationModalOpen}
						setUnsavedChanges={setUnsavedChanges}
						expectationInputs={
							selectedSampleInstance?.sampleTestInfo?.expectationInputs || {}
						}
						setExpectationInputs={(updatedExpectationInputs) => {
							if (!selectedSampleInstance) return;

							saveExpectations(updatedExpectationInputs);
						}}
						fieldsNeedingExpectations={fieldsNeedingExpectations}
					/>
				</div>
			)}
		</>
	);
}
